import { Editor } from '@monaco-editor/react';
import {
  Braces,
  Check,
  Copy,
  FileArchive,
  FileDown,
  FileUp,
  Moon,
  Sun,
} from 'lucide-react';
import React from 'react';
import { FileConvertor } from '~/components/FileConvertor';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import { useJsonEditor } from '~/hooks/useJsonEditor';
import { useTheme } from '~/hooks/useTheme';

const JsonEditor: React.FC<{
  id: string;
  code: string;
  setCode: (value: string) => void;
}> = ({ id, setCode, code }) => {
  const {
    minifyJson,
    formatJson,
    exportJson,
    uploadFile,
    copyToClipboard,
    handleEditorChange,
    handleEditorDidMount,
    editorOptions,
    fileInputRef,
    copied,
    error,
  } = useJsonEditor({ id, code, setCode });

  const { isDark, toggleTheme } = useTheme();

  return (
    <Card className='flex flex-1 p-4 overflow-hidden'>
      <div className='flex gap-4 flex-wrap w-full'>
        <Button variant='outline' size='sm' onClick={copyToClipboard}>
          {copied ? <Check /> : <Copy />}
          Copy
        </Button>
        <Button variant='outline' size='sm' onClick={minifyJson}>
          <FileArchive />
          Minify
        </Button>
        <Button variant='outline' size='sm' onClick={formatJson}>
          <Braces />
          Format
        </Button>
        <Button
          variant='outline'
          size='sm'
          onClick={() => (fileInputRef.current! as HTMLInputElement).click()}
        >
          <input
            type='file'
            hidden
            ref={fileInputRef}
            accept='.json'
            onInput={uploadFile}
          />
          <FileUp />
          Import
        </Button>
        <Button variant='outline' size='sm' onClick={exportJson}>
          <FileDown />
          Export
        </Button>
        <FileConvertor />
        <Button variant='outline' size='sm' onClick={toggleTheme}>
          {isDark ? <Sun /> : <Moon />}
          {isDark ? 'Light' : 'Dark'}
        </Button>

        <div className='ms-auto flex items-center gap-2'>
          <div
            className={`w-2 h-2 rounded-full ${error ? 'bg-red-500' : 'bg-green-500'}`}
          ></div>
          <span
            className={`text-sm ${error ? 'text-red-500' : 'text-green-500'}`}
          >
            {error ? 'Invalid JSON' : 'Valid JSON'}
          </span>
        </div>
      </div>
      <Editor
        className='border'
        height='100%'
        width='100%'
        language='json'
        theme={isDark ? 'vs-dark' : 'vs-light'}
        options={editorOptions}
        value={code}
        onChange={handleEditorChange}
        onMount={handleEditorDidMount}
      />
    </Card>
  );
};

export default JsonEditor;
